import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LazyVideo from '../components/LazyVideo';
import { imgUrl } from '../lib/img';
import './home.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────
   Homepage v2 — every media + text slot is CMS-driven.
   Only the Diosa Conviction line and the wordmark are fixed in code.
   ──────────────────────────────────────────────────────────────── */
const TEXT_DEFAULTS = {
  'film-1-caption':         'Fashion Film — No. 01',
  'hero-designer-headline': 'Designer Wear',
  'hero-designer-subline':  'One-of-one pieces, cut and finished in the Atelier.',
  'hero-designer-button':   'Discover the Atelier',
  'hero-designer-link':     '/atelier',
  'row-a-heading':          'Discover',
  'row-b-heading':          'The Edit',
  'edit-1-heading':         'Worn once.\nRemembered always.',
  'edit-1-body':            'Every Diosa piece begins as a conversation — between fabric and light, between the woman and the room she is about to enter.',
  'edit-1-button':          'Explore The Collection',
  'edit-1-link':            '/products',
  'film-2-button':          'Enter The Shop',
  'edit-2-heading':         'The details nobody\nphotographs.',
  'edit-2-body':            'Thread, hardware, weight and finish — the parts of a garment everybody feels and nobody sees.',
  'edit-2-button':          'See the Craft',
  'edit-2-link':            '/products',
  'atelier-film-button':    'Request an Appointment',
  'atelier-hero-text':      'The Atelier — by appointment only',
  'atelier-handbag-eyebrow':'The Handbag Edit',
  'atelier-handbag-heading':'Where craft meets everyday',
  'atelier-handbag-body':   'Every DIOSA handbag begins as a private conversation between hand and hide. Shaped, saddle-stitched and finished by artisans over multiple days — pieces you carry into every room, every ritual.',
  'handbag-film-caption':   'The Handbag Edit',
  'handbag-cta-label':      'Discover the Handbag Edit',
  'about-heading':          'The House of Diosa',
  'about-body':             'Diosa Forever was born from the ancient spirit of feminine power and modern luxury. Every piece is a testament to craftsmanship and heritage — cut by hand, finished by eye, and made for the woman who knows her worth. Our ateliers work in small numbers, because an heirloom cannot be hurried.',
  'about-button':           'Our Story',
  'about-link':             '/court',
  'footer-heading':         'The Court remembers you.',
  'footer-button':          'Join The Inner Circle',
  'footer-address':         'Ananta Stanford, Gotri\nFirst floor, above HDFC Bank\nVadodara, India',
  'conviction-quote':       'Fashion is not decoration.\nIt is declaration.',
  'conviction-author':      'The Diosa Conviction',
  'footer-link-1-label':    'The Collection', 'footer-link-1-url': '/products',
  'footer-link-2-label':    'The Atelier',    'footer-link-2-url': '/atelier',
  'footer-link-3-label':    'Our Story',      'footer-link-3-url': '/court',
  'footer-copyright':       '© 2026 18M Luxury Tech Pvt. Ltd. All Rights Reserved.',
  'footer-tagline':         'She Was Always Her',
};

const MID = (id) => `home:media:${id}`;
const TID = (id) => `home:text:${id}`;

/* Social platforms for the footer Follow column (block_id home:social:<key>). */
const HOME_SOCIALS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook',  label: 'Facebook' },
  { key: 'youtube',   label: 'YouTube' },
  { key: 'whatsapp',  label: 'WhatsApp' },
  { key: 'tiktok',    label: 'TikTok' },
  { key: 'pinterest', label: 'Pinterest' },
  { key: 'twitter',   label: 'X' },
  { key: 'linkedin',  label: 'LinkedIn' },
];

export default function Home() {
  const [blocks, setBlocks] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [dynamicRooms, setDynamicRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rowA, setRowA] = useState([]);
  const [rowB, setRowB] = useState([]);
  const [rowsReady, setRowsReady] = useState(false);
  const preloaderRef = useRef(null);
  const catScrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/cms/blocks`).then(({ data }) => {
      if (cancelled) return;
      const idx = {};
      (data || []).forEach(b => { if (b?.block_id) idx[b.block_id] = b; });
      setBlocks(idx);
    }).catch(() => {}).finally(() => !cancelled && setLoaded(true));
    axios.get(`${API}/api/rooms`).then(({ data }) => { if (!cancelled) setDynamicRooms(Array.isArray(data) ? data : []); }).catch(() => {});
    axios.get(`${API}/api/categories`).then(({ data }) => { if (!cancelled) setCategories(Array.isArray(data) ? data : []); }).catch(() => {});

    /* Discover + The Edit — deterministic homepage rows. Featured first,
       then newest, always the same order across refreshes. If the primary
       endpoint fails (network flake / cold start), fall back to the generic
       products list so the section is never blank. */
    const loadRows = async () => {
      try {
        const { data } = await axios.get(`${API}/api/home/rows`);
        if (cancelled) return;
        setRowA(Array.isArray(data.discover) ? data.discover : []);
        setRowB(Array.isArray(data.edit) ? data.edit : []);
      } catch {
        // Fallback: pull 8 most-recent products directly. Same shape.
        try {
          const { data } = await axios.get(`${API}/api/products?limit=8`);
          if (cancelled) return;
          const list = data.products || [];
          setRowA(list.slice(0, 4));
          setRowB(list.slice(4, 8).length ? list.slice(4, 8) : list.slice(0, 4).slice().reverse());
        } catch {
          if (!cancelled) { setRowA([]); setRowB([]); }
        }
      } finally {
        if (!cancelled) setRowsReady(true);
      }
    };
    loadRows();

    return () => { cancelled = true; };
  }, []);

  /* Live update: when any CMS block is saved (from the admin tab or the
     on-site Edit drawer) refetch blocks so the homepage updates without a reload. */
  useEffect(() => {
    const reload = () => axios.get(`${API}/api/cms/blocks`).then(({ data }) => {
      const idx = {}; (data || []).forEach(b => { if (b?.block_id) idx[b.block_id] = b; }); setBlocks(idx);
    }).catch(() => {});
    window.addEventListener('cms:updated', reload);
    return () => window.removeEventListener('cms:updated', reload);
  }, []);

  /* Text resolver: CMS text if non-empty, else the design default */
  const T = (id) => {
    const stored = blocks[TID(id)]?.content?.text;
    if (stored !== undefined && String(stored).trim() !== '') return String(stored);
    return TEXT_DEFAULTS[id] || '';
  };
  /* Media resolver — returns { url, mobile_url, kind }. On ≤640 px, callers can
     prefer `mobile_url` if set. */
  const M = (id) => blocks[MID(id)]?.content || {};
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 640;
  const [isNarrow, setIsNarrow] = useState(isMobile());
  useEffect(() => {
    const onResize = () => setIsNarrow(isMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  /* Pick mobile image on mobile viewports for image blocks (not video). Fashion
     films always use the desktop URL — per user, thumbnails stay 16:9 identical. */
  const IMG = (id) => {
    const c = M(id);
    return (isNarrow && c.mobile_url) ? c.mobile_url : (c.url || '');
  };

  useEffect(() => {
    const t = setTimeout(() => preloaderRef.current?.classList.add('hide'), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    document.querySelectorAll('.diosa-home .reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [loaded]);

  const withBreaks = (s) => String(s || '').split('\n').map((line, i, arr) => (
    <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
  ));

  /* CTA that supports internal (/path) and external links from the CMS */
  const CTA = ({ to, className, children, testId, cmsId }) => {
    const href = to || '/';
    return href.startsWith('/')
      ? <Link to={href} className={className} data-testid={testId} data-cms-id={cmsId}>{children}</Link>
      : <a href={href} className={className} data-testid={testId} data-cms-id={cmsId} target="_blank" rel="noreferrer">{children}</a>;
  };

  /* Full-width film section. The parent `.vfilm` already reserves a true 16:9
     landscape frame (see home.css), so children just fill it via `.vfilm-fill`.
     Uploading an image OR a video OR a YouTube URL to the slot renders here. */
  const Film = ({ id, caption, cta, ctaTo, hero = false, darkTone = false, testId }) => {
    const media = M(id);
    const poster = M(`${id}-poster`).url || '';
    const isVideo = media.kind === 'video' && media.url;
    return (
      <section className={`vfilm ${hero ? 'vfilm-hero' : ''} ${darkTone ? 'vfilm-dark' : ''}`} data-testid={testId} data-cms-id={`home:media:${id}`}>
        {isVideo ? (
          <LazyVideo fill aspect="16/9" src={media.url} poster={poster} tapToPlay testId={`${testId}-video`} />
        ) : media.url ? (
          <div className="vfilm-fill">
            <img src={media.url} alt="" loading={hero ? 'eager' : 'lazy'} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} data-testid={`${testId}-image`} />
          </div>
        ) : (
          <div className="slot vfilm-fill" data-label={`FILM · ${id}`} />
        )}
        {darkTone && <div className="vfilm-scrim" />}
        {caption && <div className="vfilm-cap" data-testid={`${testId}-caption`}>{caption}</div>}
        {cta && (
          <div className="vfilm-cta">
            <CTA to={ctaTo} className="btn outline-light" testId={`${testId}-cta`}>{cta}</CTA>
          </div>
        )}
      </section>
    );
  };

  const scrollCats = (dir) => {
    catScrollRef.current?.scrollBy({ left: dir * 420, behavior: 'smooth' });
  };

  /* Rooms — always renders. Fall back to categories if the /api/rooms call
     returns empty (was disappearing on refresh like Discover/Edit did). */
  const roomsToRender = dynamicRooms.length > 0
    ? dynamicRooms.map(r => ({ slug: (r.slug || r.name || '').toLowerCase(), label: r.name, image: r.image || '' }))
    : categories.slice(0, 6).map(c => ({ slug: c.slug || (c.name || '').toLowerCase(), label: c.name, image: c.image || '' }));

  return (
    <div className="diosa-home">
      {/* Preloader */}
      <div className="preloader" ref={preloaderRef}>
        <div className="pre-word">Diosa Forever</div>
        <div className="pre-bar"><span /></div>
      </div>

      {/* 1 · FASHION FILM */}
      <Film id="home-film-1" caption={T('film-1-caption')} hero testId="sec-film-1" />

      {/* 2 · DESIGNER WEAR hero moved down to introduce the Atelier (see §9b). */}

      {/* 3 · CATEGORIES — horizontal scroll, arrows on hover. Always rendered so the
          section doesn't "vanish on reload" if /api/categories is briefly slow. */}
      <section className="cats-home reveal" data-testid="sec-categories">
        <div className="eyebrow"><span className="num">I</span><span className="name">Shop by Category</span><span className="rule" /></div>
        <div className="cats-home-wrap">
          <button className="cat-arrow left" onClick={() => scrollCats(-1)} aria-label="Scroll left" data-testid="cats-arrow-left"><ChevronLeft size={22} strokeWidth={1} /></button>
          <div className="cats-home-scroll" ref={catScrollRef} data-testid="home-cats-scroll">
            {(categories.length > 0 ? categories : [{ id: 'ph', name: '' }, { id: 'ph2', name: '' }, { id: 'ph3', name: '' }, { id: 'ph4', name: '' }]).map(c => (
              <Link key={c.id || c.name} to={c.name ? `/products?category=${encodeURIComponent(c.name)}` : '/products'} className="cat-card"
                    data-testid={c.name ? `home-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}` : `home-cat-placeholder-${c.id}`}>
                <div className={`slot ${c.image ? 'filled' : ''}`} data-label={c.name || '—'}>
                  {c.image && <img src={c.image} alt={c.name} loading="lazy" />}
                </div>
                <h4>{c.name || '\u00A0'}</h4>
              </Link>
            ))}
          </div>
          <button className="cat-arrow right" onClick={() => scrollCats(1)} aria-label="Scroll right" data-testid="cats-arrow-right"><ChevronRight size={22} strokeWidth={1} /></button>
        </div>
      </section>

      {/* 4 · PRODUCT ROW A — Discover — always renders (skeleton → real products) */}
      <ProductRow heading={T('row-a-heading')} products={rowA} testId="sec-row-a" exploreTo="/products" ready={rowsReady} cmsId="home:text:row-a-heading" />

      {/* 5 · PRODUCT ROW B — The Edit — always renders (skeleton → real products) */}
      <ProductRow heading={T('row-b-heading')} products={rowB} testId="sec-row-b" exploreTo="/products" ready={rowsReady} cmsId="home:text:row-b-heading" />

      {/* 6 · CURATED ROOMS — always renders; falls back to categories */}
      {roomsToRender.length > 0 ? (
        <section className="rooms reveal" id="rooms" data-testid="sec-rooms">
          <div className="eyebrow"><span className="num">II</span><span className="name">Curated Rooms</span><span className="rule" /></div>
          <div className="rooms-scroll"
            style={{ display: 'flex', gap: 20, overflowX: 'auto', padding: '4px 20px 24px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            data-testid="home-rooms-scroll">
            {roomsToRender.map(r => (
              <Link key={r.slug} to={`/rooms/${r.slug}`} className="room-card"
                style={{ flex: '0 0 auto', width: 'clamp(220px, 30vw, 340px)', scrollSnapAlign: 'start', textDecoration: 'none', color: 'inherit' }}
                data-testid={`home-room-card-${r.slug}`}>
                <div className={`slot ${r.image ? 'filled' : ''}`} data-label={r.label} style={{ aspectRatio: '3/4' }}>
                  {r.image && <img src={r.image} alt={r.label} loading="lazy" />}
                </div>
                <span className="arr">↗</span>
                <h3>{r.label}</h3>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section className="rooms reveal" id="rooms" data-testid="sec-rooms-empty" style={{ padding: '40px var(--pad-x)' }}>
          <div className="eyebrow"><span className="num">II</span><span className="name">Curated Rooms</span><span className="rule" /></div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: 22, color: '#7A756B', textAlign: 'center', margin: '24px 0' }}>Curating your rooms — check back shortly.</p>
        </section>
      )}

      {/* 7 · EDITORIAL SPLIT — image LEFT */}
      <section className="split reveal" data-testid="sec-edit-1">
        <div className={`slot ${IMG('edit-1-image') ? 'filled' : ''}`} data-label="EDITORIAL 1 · image" data-cms-id="home:media:edit-1-image">
          {IMG('edit-1-image') && <img src={IMG('edit-1-image')} alt="" loading="lazy" />}
        </div>
        <div>
          <div className="eyebrow"><span className="num">III</span><span className="name">Editorial</span><span className="rule" /></div>
          <h2 data-testid="text-edit-1-heading" data-cms-id="home:text:edit-1-heading">{withBreaks(T('edit-1-heading'))}</h2>
          <p data-testid="text-edit-1-body" data-cms-id="home:text:edit-1-body">{T('edit-1-body')}</p>
          <CTA to={T('edit-1-link')} className="btn gold" testId="edit-1-cta" cmsId="home:text:edit-1-button">{T('edit-1-button')}</CTA>
        </div>
      </section>

      {/* 8 · HORIZONTAL VIDEO → SHOP */}
      <Film id="home-film-2" cta={T('film-2-button')} ctaTo="/products" testId="sec-film-2" />

      {/* 9 · EDITORIAL SPLIT #2 — image RIGHT */}
      <section className="split flip reveal" data-testid="sec-edit-2">
        <div>
          <div className="eyebrow"><span className="num">IV</span><span className="name">Editorial</span><span className="rule" /></div>
          <h2 data-testid="text-edit-2-heading" data-cms-id="home:text:edit-2-heading">{withBreaks(T('edit-2-heading'))}</h2>
          <p data-testid="text-edit-2-body" data-cms-id="home:text:edit-2-body">{T('edit-2-body')}</p>
          <CTA to={T('edit-2-link')} className="btn gold" testId="edit-2-cta" cmsId="home:text:edit-2-button">{T('edit-2-button')}</CTA>
        </div>
        <div className={`slot ${IMG('edit-2-image') ? 'filled' : ''}`} data-label="EDITORIAL 2 · image" data-cms-id="home:media:edit-2-image">
          {IMG('edit-2-image') && <img src={IMG('edit-2-image')} alt="" loading="lazy" />}
        </div>
      </section>

      {/* 9b · DESIGNER WEAR — introduces the Atelier, sits directly above its film */}
      <section className="dhero reveal" data-testid="sec-designer-hero">
        {IMG('hero-designer')
          ? <img src={IMG('hero-designer')} alt="" loading="lazy" className="dhero-img" data-cms-id="home:media:hero-designer" />
          : <div className="slot dhero-img" data-label="DESIGNER WEAR · full-width horizontal image" data-cms-id="home:media:hero-designer" />}
        <div className="dhero-scrim" />
        <div className="dhero-inner">
          <h2 data-testid="text-hero-designer-headline" data-cms-id="home:text:hero-designer-headline">{withBreaks(T('hero-designer-headline'))}</h2>
          <p data-testid="text-hero-designer-subline" data-cms-id="home:text:hero-designer-subline">{T('hero-designer-subline')}</p>
          <CTA to={T('hero-designer-link')} className="btn outline-light" testId="designer-hero-cta" cmsId="home:text:hero-designer-button">{T('hero-designer-button')}</CTA>
        </div>
      </section>

      {/* 10 · THE ATELIER — VIDEO */}
      <Film id="atelier-film" darkTone cta={T('atelier-film-button')} ctaTo="/atelier/appointment" testId="sec-atelier-film" />

      {/* 11 · ATELIER HERO IMAGE */}
      <section className="dhero atelier-hero reveal" data-testid="sec-atelier-hero">
        {IMG('atelier-hero')
          ? <img src={IMG('atelier-hero')} alt="" loading="lazy" className="dhero-img" data-cms-id="home:media:atelier-hero" />
          : <div className="slot dhero-img" data-label="ATELIER · full-width horizontal image" data-cms-id="home:media:atelier-hero" />}
        <div className="dhero-scrim soft" />
        {T('atelier-hero-text') && (
          <div className="dhero-inner center">
            <p className="dhero-line" data-testid="text-atelier-hero-text" data-cms-id="home:text:atelier-hero-text">{T('atelier-hero-text')}</p>
          </div>
        )}
      </section>

      {/* 11b · ATELIER → HANDBAG bridge — title + body copy sits between the
          two film sections so the transition doesn't feel jarring. All three
          strings are editable in CMS Control (`text:atelier-handbag-*`). */}
      <section className="atelier-handbag-bridge reveal" data-testid="sec-handbag-intro">
        <div className="eyebrow"><span className="num">IV</span><span className="name" data-cms-id="home:text:atelier-handbag-eyebrow">{T('atelier-handbag-eyebrow')}</span><span className="rule" /></div>
        <h2 className="ah-heading" data-testid="text-atelier-handbag-heading" data-cms-id="home:text:atelier-handbag-heading">{T('atelier-handbag-heading')}</h2>
        <p className="ah-body" data-testid="text-atelier-handbag-body" data-cms-id="home:text:atelier-handbag-body">{T('atelier-handbag-body')}</p>
      </section>

      {/* 12 · HANDBAG HERO FILM with CTA */}
      <Film id="handbag-film" caption={T('handbag-film-caption')} cta={T('handbag-cta-label')} ctaTo="/products?category=Bags" testId="sec-handbag-film" />

      {/* 13 · REELS ROW — 3 vertical 9:16, tap to play */}
      <section className="reels reveal" data-testid="sec-reels">
        <div className="eyebrow"><span className="num">V</span><span className="name">In Motion</span><span className="rule" /></div>
        <div className="reels-row">
          {[1, 2, 3].map(n => {
            const media = M(`reel-${n}`);
            const poster = M(`reel-${n}-poster`).url || '';
            return (
              <div key={n} className="reel" data-testid={`reel-${n}`} data-cms-id={`home:media:reel-${n}`}>
                {media.kind === 'video' && media.url
                  ? <LazyVideo aspect="9/16" src={media.url} poster={poster} tapToPlay testId={`reel-${n}-video`} />
                  : media.url
                    ? <img src={media.url} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '9 / 16', objectFit: 'cover' }} />
                    : <div className="slot" data-label={`REEL ${n} · 9:16`} style={{ aspectRatio: '9 / 16' }} />}
              </div>
            );
          })}
        </div>
      </section>

      {/* 14 · ABOUT DIOSA FOREVER & CRAFTSMANSHIP */}
      <section className="split about-split reveal" data-testid="sec-about">
        <div className={`slot ${IMG('about-image') ? 'filled' : ''}`} data-label="ABOUT · image" style={{ aspectRatio: '4/5' }} data-cms-id="home:media:about-image">
          {IMG('about-image') && <img src={IMG('about-image')} alt="" loading="lazy" />}
        </div>
        <div>
          <div className="eyebrow"><span className="num">VI</span><span className="name">The House</span><span className="rule" /></div>
          <h2 data-testid="text-about-heading" data-cms-id="home:text:about-heading">{withBreaks(T('about-heading'))}</h2>
          <p data-testid="text-about-body" data-cms-id="home:text:about-body">{T('about-body')}</p>
          <CTA to={T('about-link')} className="btn gold" testId="about-cta" cmsId="home:text:about-button">{T('about-button')}</CTA>
        </div>
      </section>

      {/* 14b · CRAFT — 4 cards (thread, hardware, finish, weight). CMS-driven. */}
      <section className="craft-grid reveal" data-testid="sec-craft">
        <div className="eyebrow"><span className="num">VII</span><span className="name">The Craft</span><span className="rule" /></div>
        <div className="craft-row">
          {[1, 2, 3, 4].map(n => {
            const media = M(`craft-${n}`);
            const heading = T(`craft-${n}-heading`) || `Craft ${n}`;
            const body = T(`craft-${n}-body`) || '';
            return (
              <div key={n} className="craft-card" data-testid={`craft-${n}`}>
                <div className="slot" data-label={`CRAFT ${n} · 4/5`} style={{ aspectRatio: '4 / 5', position: 'relative', overflow: 'hidden' }} data-cms-id={`home:media:craft-${n}`}>
                  {media.url && <img src={media.url} alt={heading} loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <p className="craft-heading" data-testid={`craft-${n}-heading`}>{heading}</p>
                {body && <p className="craft-body" data-testid={`craft-${n}-body`}>{body}</p>}
              </div>
            );
          })}
        </div>
      </section>

      {/* 15 · DIOSA CONVICTION — CMS-editable */}
      <section className="philosophy reveal" data-testid="sec-conviction">
        <p data-testid="text-conviction-quote" data-cms-id="home:text:conviction-quote">
          {withBreaks(T('conviction-quote'))}
          <span data-testid="text-conviction-author" data-cms-id="home:text:conviction-author">{T('conviction-author')}</span>
        </p>
      </section>

      {/* 16 · FOOTER */}
      <footer className="dfoot" id="footer">
        <div className="foot-top">
          <h3 data-testid="text-footer-heading" data-cms-id="home:text:footer-heading">{T('footer-heading')}</h3>
          <a href="#" className="btn light" data-testid="text-footer-button" data-cms-id="home:text:footer-button">{T('footer-button')}</a>
        </div>
        <div className="foot-cols">
          <div>
            <p className="h">Visit</p>
            <p className="foot-address" data-testid="text-footer-address" data-cms-id="home:text:footer-address" style={{ opacity: .75, fontSize: 13, lineHeight: 1.9, whiteSpace: 'pre-line' }}>
              {T('footer-address')}
            </p>
          </div>
          <div>
            <p className="h">House</p>
            {[1, 2, 3].map(n => (
              <Link key={n} to={T(`footer-link-${n}-url`) || '/'} data-cms-id={`home:text:footer-link-${n}-label`}>{T(`footer-link-${n}-label`)}</Link>
            ))}
          </div>
          <div>
            <p className="h">Follow</p>
            {(() => {
              const links = HOME_SOCIALS
                .map(s => ({ label: s.label, url: (blocks[`home:social:${s.key}`]?.content?.url || '').trim() }))
                .filter(s => s.url);
              const shown = links.length ? links : [
                { label: 'Instagram', url: '#' }, { label: 'WhatsApp', url: '#' }, { label: 'YouTube', url: '#' },
              ];
              return shown.map((s, i) => (
                <a key={i} href={s.url} target={s.url === '#' ? undefined : '_blank'} rel="noopener noreferrer" data-testid={`home-social-${s.label.toLowerCase()}`}>{s.label}</a>
              ));
            })()}
          </div>
        </div>
        <nav className="foot-legal" style={{ display: 'flex', gap: '22px', flexWrap: 'wrap', justifyContent: 'center', padding: '0 0 18px', fontSize: '11px', letterSpacing: '.14em', textTransform: 'uppercase', opacity: .8 }}>
          <Link to="/terms" data-testid="footer-terms">Terms &amp; Conditions</Link>
          <Link to="/privacy" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/returns" data-testid="footer-returns">Return &amp; Refund Policy</Link>
        </nav>
        <div className="foot-bottom">
          <span data-cms-id="home:text:footer-copyright">{T('footer-copyright')}</span>
          <span data-cms-id="home:text:footer-tagline">{T('footer-tagline')}</span>
        </div>
      </footer>
    </div>
  );
}

/* ─── Product row — luxury 3D card, portrait image, aligned name/price.
   ALWAYS renders 4 cells: skeleton placeholders while `ready=false` or the
   data is empty, real products once they arrive. Layout reserves space so
   the page never jumps and the section never collapses to nothing. */
const ProductRow = ({ heading, products, testId, exploreTo, ready = true, cmsId }) => {
  const items = (products || []).slice(0, 4);
  const filler = Array.from({ length: Math.max(0, 4 - items.length) });
  const hasRealProducts = items.length > 0;
  return (
    <section className="prod-row reveal" data-testid={testId}>
      {heading && <p className="prod-row-head" data-testid={`${testId}-heading`} data-cms-id={cmsId}>{heading}</p>}
      <div className="prod-row-grid" data-testid={`${testId}-grid`}>
        {items.map(p => (
          <Link key={p.id} to={`/products/${p.id}`} className="prod-card" data-testid={`${testId}-product-${p.id}`}>
            <div className="prod-card-media">
              {(p.images || [])[0]
                ? <img src={imgUrl(p.images[0], 600)} alt={p.name} loading="lazy" decoding="async" />
                : <div className="prod-card-placeholder">{p.name || '—'}</div>}
              {p.tier === 'Signature' && <span className="prod-card-tag">Signature</span>}
            </div>
            <div className="prod-card-meta">
              <span className="prod-card-name">{p.name}</span>
              <span className="prod-card-price">₹{Number(p.price || 0).toLocaleString('en-IN')}</span>
            </div>
          </Link>
        ))}
        {filler.map((_, i) => (
          <div key={`ph-${i}`} className={`prod-card prod-card-skeleton ${ready ? 'faded' : ''}`} aria-hidden="true" data-testid={`${testId}-skeleton-${i}`}>
            <div className="prod-card-media">
              <div className="prod-card-shimmer" />
            </div>
            <div className="prod-card-meta">
              <span className="prod-card-name">&nbsp;</span>
              <span className="prod-card-price">&nbsp;</span>
            </div>
          </div>
        ))}
      </div>
      {hasRealProducts && (
        <div className="prod-row-explore-inline" data-testid={`${testId}-explore`}>
          <Link to={exploreTo || '/products'} className="btn gold" data-testid={`${testId}-explore-btn`}>Explore More</Link>
        </div>
      )}
    </section>
  );
};
